import { yupResolver } from '@hookform/resolvers/yup';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { TextFormField } from '~components/FormFields/TextFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { useCompany } from '~hooks/useCompany/useCompany';
import { useStores } from '~store';
import UserStore from '~store/UserStore';
import { alert, AlertTypes } from '~types/AlertTypes';

const settingSchema = yup.object().shape({
  defaultGeofenceDelaySeconds: yup.number().typeError('Numbers only.'),
});

type SettingDTO = yup.InferType<typeof settingSchema>;

function GeofenceTimerSetting() {
  const { userStore, toasterStore } = useStores();
  const { updateCompany } = useCompany();
  const [isSaving, setIsSaving] = useState(false);
  const [inSaveMode, setInSaveMode] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SettingDTO>({
    resolver: yupResolver(settingSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      defaultGeofenceDelaySeconds: userStore.userCompany.defaultGeofenceDelaySeconds || 0,
    },
  });

  const saveCompanyGeofenceTimer = (data: SettingDTO) => {
    setIsSaving(true);
    updateCompany({
      company: {
        id: userStore.userCompany.id,
        defaultGeofenceDelaySeconds: data.defaultGeofenceDelaySeconds,
      },
    })
      .then(() => {
        setInSaveMode(false);
        toasterStore.push(
          alert(
            t('administration.configuration.configuration_saved'),
            AlertTypes.success,
          ),
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  return (
    <>
      <Typography sx={{ mb: 1 }}>
        {t('administration.configuration.settings.geofence_timer.setting_subtitle')}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ sm: 3 }}>
          <TextFormField
            control={control}
            errors={errors}
            disabled={!inSaveMode}
            name="defaultGeofenceDelaySeconds"
            type="number"
            label={`${t('administration.configuration.settings.geofence_timer.default_geofence_delay_seconds')}`}
          />
        </Grid>

        <Grid sx={{ alignSelf: 'flex-end', mb: 1 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {inSaveMode && (
              <SmallButton
                color="secondary"
                variant="outlined"
                onClick={() => {
                  setInSaveMode(false);
                  reset({
                    defaultGeofenceDelaySeconds:
                      userStore.userCompany.defaultGeofenceDelaySeconds ?? 0,
                  });
                }}
                startIcon={<Close />}
              >
                {t('actions.cancel')}
              </SmallButton>
            )}
            <SmallLoadingButton
              loading={isSaving}
              variant="contained"
              color="brandV2Yellow"
              startIcon={inSaveMode ? <Check /> : null}
              onClick={
                inSaveMode
                  ? handleSubmit(saveCompanyGeofenceTimer)
                  : () => setInSaveMode(true)
              }
            >
              {inSaveMode ? t('actions.save') : t('actions.edit')}
            </SmallLoadingButton>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}

export default {
  component: GeofenceTimerSetting,
  canAccess: (userStore: UserStore) => {
    return userStore.getPermissions().canEditCompany;
  },
};
